import {
  type GetProductsFullRes,
  type GetProductsRes,
  type MarketplaceProductDTO,
  type OldProductDTO,
  type ProductAutocompleteDTO,
  type ProductLightDTO,
  type ProductSearchResponseDTO,
  ProductType,
  type StoreProductFilterDTO,
  type StoreSearchProductFilterDTO
} from 'ecosystem'
import { createLogger, createUrl } from 'shared-utils'
import type { UrlConfig } from '../utils/fetching'
import {
  createEndpointError,
  fetchPropsFactory,
  storefrontApiCall,
  urlWithQueryParamsFactory
} from '../utils/fetching'
import { BASE_STOREFRONT_ENDPOINT } from './storeEndpoint'

const productsEndpoint = `${BASE_STOREFRONT_ENDPOINT}/products`

export type ProductQueryParams = NonNullable<any>

interface RelatedProductsQueryParams {
  ids: string
  customerId?: string
}

const logger = createLogger('storfront-api/products')

export async function checkoutRelatedProducts(
  config: UrlConfig<RelatedProductsQueryParams>
): Promise<ProductLightDTO[] | null> {
  const url = urlWithQueryParamsFactory<ProductQueryParams>(`${productsEndpoint}/related`, config)
  const props = fetchPropsFactory()
  const { data: products, error } = await storefrontApiCall<ProductLightDTO[]>(url, props)

  if (error) {
    return createEndpointError('related products', error)
  }

  return products
}

export async function listProducts(
  filter: Partial<StoreProductFilterDTO>,
  config?: UrlConfig<ProductQueryParams>
): Promise<GetProductsRes> {
  const url = urlWithQueryParamsFactory<ProductQueryParams>(`${productsEndpoint}/filter`, config)

  const filterWithType: Partial<StoreProductFilterDTO> = {
    types: [ProductType.REGULAR, ProductType.BUNDLE, ProductType.CONFIGURATION],
    ...filter
  }

  const props = fetchPropsFactory({
    props: {
      method: 'POST',
      body: JSON.stringify(filterWithType)
    }
  })
  const { data: products, error } = await storefrontApiCall<GetProductsRes>(url, props)

  if (error || !products) {
    logger.error('listProducts [ERROR]', {
      url,
      requestProps: props,
      error
    })
    return createEndpointError('list products', error)
  }

  return products
}

export async function listProductsFull(
  filter: Partial<StoreProductFilterDTO>,
  config?: UrlConfig<ProductQueryParams>
): Promise<GetProductsFullRes> {
  const url = urlWithQueryParamsFactory<ProductQueryParams>(
    `${productsEndpoint}/filter/full`,
    config
  )

  const filterWithType: Partial<StoreProductFilterDTO> = {
    types: [ProductType.REGULAR, ProductType.BUNDLE, ProductType.CONFIGURATION],
    ...filter
  }

  const props = fetchPropsFactory({
    props: {
      method: 'POST',
      body: JSON.stringify(filterWithType)
    }
  })
  const { data: productsFullResponse, error } = await storefrontApiCall<GetProductsFullRes>(
    url,
    props
  )

  if (error || !productsFullResponse) {
    return createEndpointError('list full products', error || 'unknown error')
  }

  return productsFullResponse
}

export async function productById(id: string, customerId?: string): Promise<OldProductDTO | null> {
  const url = createUrl(`${productsEndpoint}/${id}`, { customerId })
  const props = fetchPropsFactory()
  const { data: product } = await storefrontApiCall<OldProductDTO>(url, props)
  return product
}

export async function productBySlug(
  slug: string,
  customerId?: string
): Promise<OldProductDTO | null> {
  const url = createUrl(`${productsEndpoint}/slug/${slug}`, { customerId })
  const props = fetchPropsFactory()
  const { data: product } = await storefrontApiCall<OldProductDTO>(url, props)
  return product
}

export async function productSlugs(): Promise<string[]> {
  const url = `${productsEndpoint}/slugs`
  const props = fetchPropsFactory()
  const { data: products, error } = await storefrontApiCall<string[]>(url, props)

  if (error || !products) {
    return createEndpointError('slugs endpoint error', error)
  }

  return products
}

export async function variantsByGroupId(
  variantGroupId: string,
  customerId?: string
): Promise<OldProductDTO[] | null> {
  const props = fetchPropsFactory()
  const url = createUrl(`${productsEndpoint}/variant/${variantGroupId}`, { customerId })
  const { data: variants, error } = await storefrontApiCall<OldProductDTO[]>(url, props)

  if (error) {
    return createEndpointError('variants by group ID', error)
  }

  return variants
}

export async function searchProducts(
  filter: StoreSearchProductFilterDTO,
  config?: UrlConfig<ProductQueryParams>
): Promise<ProductSearchResponseDTO> {
  const url = urlWithQueryParamsFactory<ProductQueryParams>(`${productsEndpoint}/search`, config)

  const props = fetchPropsFactory({
    props: {
      method: 'POST',
      body: JSON.stringify(filter)
    }
  })
  const { data, error } = await storefrontApiCall<ProductSearchResponseDTO>(url, props)
  if (error || !data) {
    return createEndpointError('search products')
  }

  return data
}

export async function searchProductsAutocomplete(
  text: string,
  limit?: number,
  customerId?: string
): Promise<ProductAutocompleteDTO[]> {
  const url = createUrl(`${productsEndpoint}/search/autocomplete`, { text, limit, customerId })
  const props = fetchPropsFactory()
  const { data, error } = await storefrontApiCall<ProductAutocompleteDTO[]>(url, props)
  if (error || !data) {
    return createEndpointError('search products autocomplete')
  }

  return data
}

export async function getMarketplaceProducts(
  slug: string,
  customerId?: string
): Promise<MarketplaceProductDTO[] | null> {
  const url = createUrl(`${productsEndpoint}/slug/${slug}/marketplace`, { customerId })
  const props = fetchPropsFactory()

  const { data, error } = await storefrontApiCall<MarketplaceProductDTO[]>(url, props)
  if (error || !data) {
    return createEndpointError('Marketplace products')
  }

  return data
}
